import React, { useState, useEffect, useCallback } from 'react';
import { PlusIcon, TrashIcon, PencilIcon, XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import { staffPicksService, StaffPick } from '../services/staffPicks';
import DressPickerModal from './DressPickerModal';
import AuthenticatedImage from './AuthenticatedImage';

interface StaffPicksManagerProps {
  customerId: string;
  customerName: string;
}

const StaffPicksManager: React.FC<StaffPicksManagerProps> = ({ customerId, customerName }) => {
  const [picks, setPicks] = useState<StaffPick[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editNoteValue, setEditNoteValue] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const fetchPicks = useCallback(async () => {
    setLoading(true);
    try {
      const response = await staffPicksService.getCustomerPicks(customerId);
      setPicks(response.data);
    } catch (error) {
      console.error('Failed to fetch staff picks:', error);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    fetchPicks();
  }, [fetchPicks]);

  const handleAddDresses = async (selections: Array<{ dressId: string; note?: string }>) => {
    // Add all dresses in parallel
    const newPicks = await Promise.all(
      selections.map(({ dressId, note }) =>
        staffPicksService.addPick(customerId, { dressId, note })
      )
    );
    setPicks((prev) => [...prev, ...newPicks]);
  };

  const handleRemovePick = async (dressId: string) => {
    setRemovingId(dressId);
    try {
      await staffPicksService.removePick(customerId, dressId);
      setPicks((prev) => prev.filter((p) => p.dressId !== dressId));
    } catch (error) {
      console.error('Failed to remove pick:', error);
    } finally {
      setRemovingId(null);
    }
  };

  const handleEditNote = (pick: StaffPick) => {
    setEditingNoteId(pick.id);
    setEditNoteValue(pick.note || '');
  };

  const handleSaveNote = async (pickId: string) => {
    setSavingNote(true);
    try {
      await staffPicksService.updateNote(pickId, editNoteValue);
      setPicks((prev) =>
        prev.map((p) => (p.id === pickId ? { ...p, note: editNoteValue || undefined } : p))
      );
      setEditingNoteId(null);
      setEditNoteValue('');
    } catch (error) {
      console.error('Failed to update note:', error);
    } finally {
      setSavingNote(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditNoteValue('');
  };

  const existingDressIds = picks.map((p) => p.dressId);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Staff Picks</h3>
        <div className="flex justify-center py-8">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Staff Picks</h3>
          <p className="text-sm text-gray-500">
            {picks.length} dress{picks.length !== 1 ? 'es' : ''} recommended for {customerName}
          </p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          <PlusIcon className="h-5 w-5" />
          Add Dress
        </button>
      </div>

      {/* Picks List */}
      <div className="p-6">
        {picks.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 mb-4">No dresses picked yet for this customer.</p>
            <button
              onClick={() => setIsModalOpen(true)}
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              Add the first dress
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {picks.map((pick) => (
              <div
                key={pick.id}
                className="flex items-start gap-4 p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                {/* Thumbnail */}
                <div className="w-16 h-20 flex-shrink-0 bg-gray-100 rounded overflow-hidden">
                  {pick.dress.thumbnailUrl ? (
                    <AuthenticatedImage
                      src={pick.dress.thumbnailUrl}
                      alt={pick.dress.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-gray-400 text-xs">
                      No image
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900">{pick.dress.name}</p>
                  {pick.dress.designer && (
                    <p className="text-sm text-gray-500">{pick.dress.designer.name}</p>
                  )}

                  {/* Note */}
                  {editingNoteId === pick.id ? (
                    <div className="mt-2 flex items-center gap-2">
                      <input
                        type="text"
                        value={editNoteValue}
                        onChange={(e) => setEditNoteValue(e.target.value)}
                        placeholder="Add a note..."
                        className="flex-1 text-sm border border-gray-300 rounded px-2 py-1 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                        autoFocus
                      />
                      <button
                        onClick={() => handleSaveNote(pick.id)}
                        disabled={savingNote}
                        className="p-1 text-green-600 hover:bg-green-50 rounded"
                      >
                        <CheckIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="p-1 text-gray-400 hover:bg-gray-100 rounded"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                  ) : pick.note ? (
                    <div className="mt-2 flex items-center gap-2">
                      <p className="text-sm text-primary-600 italic">"{pick.note}"</p>
                      <button
                        onClick={() => handleEditNote(pick)}
                        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleEditNote(pick)}
                      className="mt-2 text-sm text-gray-400 hover:text-gray-600"
                    >
                      + Add note
                    </button>
                  )}

                  {/* Meta */}
                  <p className="mt-2 text-xs text-gray-400">
                    Added by {pick.addedBy.name} on{' '}
                    {new Date(pick.createdAt).toLocaleDateString()}
                  </p>
                </div>

                {/* Actions */}
                <button
                  onClick={() => handleRemovePick(pick.dressId)}
                  disabled={removingId === pick.dressId}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-full flex-shrink-0"
                  title="Remove from Staff Picks"
                >
                  {removingId === pick.dressId ? (
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-red-200 border-t-red-500"></div>
                  ) : (
                    <TrashIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Dress Picker Modal */}
      <DressPickerModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSelectDresses={handleAddDresses}
        existingDressIds={existingDressIds}
      />
    </div>
  );
};

export default StaffPicksManager;
